import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ScoutMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  reported?: boolean;
  report_id?: string | null;
  created_at?: string;
}

/**
 * Loads / creates the single Scout conversation for the signed-in user
 * and persists every turn. Falls back to in-memory only on failure.
 */
export function useScoutConversation() {
  const { user } = useAuth();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ScoutMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedForUser = useRef<string | null>(null);

  // Load or create the user's single conversation + its message history.
  useEffect(() => {
    if (!user) {
      setConversationId(null);
      setMessages([]);
      setLoading(false);
      loadedForUser.current = null;
      return;
    }
    if (loadedForUser.current === user.id) return;
    loadedForUser.current = user.id;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        // Upsert conversation (unique on user_id).
        const { data: existing, error: selErr } = await supabase
          .from('scout_conversations')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        if (selErr) throw selErr;

        let convId = existing?.id ?? null;
        if (!convId) {
          const { data: inserted, error: insErr } = await supabase
            .from('scout_conversations')
            .insert({ user_id: user.id })
            .select('id')
            .single();
          if (insErr) throw insErr;
          convId = inserted.id;
        }
        if (cancelled) return;
        setConversationId(convId);

        // Load prior messages.
        const { data: rows, error: msgErr } = await supabase
          .from('scout_messages')
          .select('id, role, content, reported, report_id, created_at')
          .eq('conversation_id', convId)
          .order('created_at', { ascending: true });
        if (msgErr) throw msgErr;
        if (cancelled) return;

        const loaded: ScoutMessage[] = (rows ?? [])
          .filter((r) => r.role === 'user' || r.role === 'assistant')
          .map((r) => ({
            id: r.id,
            role: r.role as 'user' | 'assistant',
            content: r.content,
            reported: r.reported ?? false,
            report_id: r.report_id ?? null,
            created_at: r.created_at,
          }));
        setMessages(loaded);
      } catch (err) {
        console.error('[useScoutConversation] load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const persistMessage = useCallback(
    async (msg: Omit<ScoutMessage, 'id' | 'created_at'> & { page_url?: string | null }) => {
      if (!conversationId) return null;
      const { data, error } = await supabase
        .from('scout_messages')
        .insert({
          conversation_id: conversationId,
          role: msg.role,
          content: msg.content,
          reported: msg.reported ?? false,
          report_id: msg.report_id ?? null,
          page_url: msg.page_url ?? null,
        })
        .select('id, created_at')
        .single();
      if (error) {
        console.error('[useScoutConversation] persist failed', error);
        return null;
      }
      return data;
    },
    [conversationId],
  );

  const appendUserMessage = useCallback(
    async (content: string, pageUrl?: string | null) => {
      const optimistic: ScoutMessage = { role: 'user', content };
      setMessages((prev) => [...prev, optimistic]);
      const persisted = await persistMessage({ role: 'user', content, page_url: pageUrl });
      if (persisted) {
        setMessages((prev) =>
          prev.map((m) =>
            m === optimistic ? { ...m, id: persisted.id, created_at: persisted.created_at } : m,
          ),
        );
      }
    },
    [persistMessage],
  );

  const appendAssistantMessage = useCallback(
    async (content: string, meta?: { reported?: boolean; report_id?: string | null; page_url?: string | null }) => {
      const optimistic: ScoutMessage = {
        role: 'assistant',
        content,
        reported: meta?.reported ?? false,
        report_id: meta?.report_id ?? null,
      };
      setMessages((prev) => [...prev, optimistic]);
      const persisted = await persistMessage({
        role: 'assistant',
        content,
        reported: meta?.reported,
        report_id: meta?.report_id,
        page_url: meta?.page_url,
      });
      if (persisted) {
        setMessages((prev) =>
          prev.map((m) =>
            m === optimistic ? { ...m, id: persisted.id, created_at: persisted.created_at } : m,
          ),
        );
      }
    },
    [persistMessage],
  );

  const clearConversation = useCallback(async () => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    const { error } = await supabase
      .from('scout_messages')
      .delete()
      .eq('conversation_id', conversationId);
    if (error) {
      console.error('[useScoutConversation] clear failed', error);
      return;
    }
    setMessages([]);
  }, [conversationId]);

  return {
    conversationId,
    messages,
    loading,
    appendUserMessage,
    appendAssistantMessage,
    clearConversation,
  };
}
